/**
 * ValidationPennylaneQueue.js
 * 
 * Tableau "À valider" pour COMPTA FI NEW
 * Permet la validation humaine avant envoi à Pennylane
 * 
 * Fonctionnalités:
 * - Liste des transactions en attente de validation
 * - Affichage PDF, montants, catégorie, statut
 * - Actions: Modifier / Valider / Refuser
 * - Gestion des catégories pour factures existantes
 */

import { useState, useEffect, useCallback } from "react";
import getApiUrl from '../utils/getApiUrl';
import {
  ClipboardCheck, RefreshCw, Search, Filter, Eye, Check, X,
  AlertCircle, AlertTriangle, CheckCircle2, FileText, Building2,
  User, Calculator, Tag, CreditCard, ExternalLink, Edit3,
  Send, Loader2, ChevronDown, ChevronRight, Download, Trash2,
  ArrowUpRight, ArrowDownLeft, FolderOpen, Info, Sparkles, Wrench,
  Calendar, MessageSquare, Link2, Users, Paperclip, Ban
} from "lucide-react";

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Statuts possibles
const STATUS_CONFIG = {
  ready: { label: "Prêt", color: "green", icon: CheckCircle2, description: "Prêt à être envoyé" },
  missing_info: { label: "Manque info", color: "yellow", icon: AlertTriangle, description: "Informations manquantes" },
  error: { label: "Erreur", color: "red", icon: AlertCircle, description: "Erreur lors de la préparation" },
  sent: { label: "Envoyé", color: "blue", icon: Send, description: "Envoyé à Pennylane" },
  rejected: { label: "Refusé", color: "gray", icon: X, description: "Refusé manuellement" },
  existing: { label: "Existe dans PL", color: "purple", icon: ExternalLink, description: "Facture existante dans Pennylane" }
};

// Types de transactions
const TRANSACTION_TYPES = {
  expense: { label: "Décaissement", icon: ArrowUpRight, color: "red", description: "Facture fournisseur" },
  income: { label: "Encaissement", icon: ArrowDownLeft, color: "green", description: "Facture client" }
};

export default function ValidationPennylaneQueue() {
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ ready: 0, missing_info: 0, error: 0, sent: 0, existing: 0 });
  const [filter, setFilter] = useState("all"); // all, ready, missing_info, error, existing
  const [accountFilter, setAccountFilter] = useState("all"); // Filter by bank account
  const [bankAccounts, setBankAccounts] = useState([]); // List of bank accounts
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [processing, setProcessing] = useState(null); // ID of item being processed
  const [error, setError] = useState(null);
  
  // Invoice viewer modal
  const [invoiceViewerOpen, setInvoiceViewerOpen] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  
  // Attach to client modal
  const [attachClientOpen, setAttachClientOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  // Rachel IA Search
  const [rachelSearchOpen, setRachelSearchOpen] = useState(false);
  const [rachelQuery, setRachelQuery] = useState("");
  const [rachelDateFrom, setRachelDateFrom] = useState("");
  const [rachelDateTo, setRachelDateTo] = useState("");
  const [rachelSearching, setRachelSearching] = useState(false);
  const [rachelResults, setRachelResults] = useState(null);

  // Load data with server-side pagination
  const loadData = useCallback(async (page = currentPage, statusFilter = filter, searchQuery = searchTerm, accFilter = accountFilter) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Build query params for server-side pagination
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: itemsPerPage.toString()
      });
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter);
      }
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      if (accFilter && accFilter !== 'all') {
        params.append('account_filter', accFilter);
      }
      
      // Load validation queue with pagination
      const resp = await authFetch(`${API}/api/compta-new/pending?${params}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setItems(data.items || []);
        setStats(data.stats || { ready: 0, missing_info: 0, error: 0, sent: 0, existing: 0 });
        
        // Update pagination state from server
        if (data.pagination) {
          setTotalPages(data.pagination.total_pages || 1);
          setTotalItems(data.pagination.total_items || 0);
        }
        
        // Get unique bank accounts for filter
        if (data.bank_accounts && data.bank_accounts.length > 0) {
          setBankAccounts(data.bank_accounts);
        }
        
        setError(null);
      } else if (resp.status === 401) {
        console.warn("Session expirée ou invalide");
      } else {
        setError(`Erreur serveur: ${resp.status}`);
      }
      
      // Load categories (only once)
      if (categories.length === 0) {
        const catResp = await authFetch(`${API}/api/rachel-v2/pennylane-suggestions`, { headers });
        if (catResp.ok) {
          const catData = await catResp.json();
          setCategories(catData.suggestions?.categories || []);
        }
      }
    } catch (e) {
      console.error("Erreur chargement données:", e);
      setError("Erreur lors du chargement des données");
    }
    setLoading(false);
    setRefreshing(false);
  }, [currentPage, filter, searchTerm, accountFilter, categories.length]);

  useEffect(() => {
    loadData(currentPage, filter, searchTerm, accountFilter);
  }, [currentPage, filter, searchTerm, accountFilter]);

  // Debounce search to avoid too many requests
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1); // Reset to page 1 on new search
      } else {
        loadData(1, filter, searchTerm, accountFilter);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // View invoice PDF
  const handleViewInvoice = async (item) => {
    setInvoiceLoading(true);
    setInvoiceViewerOpen(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/invoice-url/${item.pennylane_transaction_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        if (data.invoice_url) {
          setInvoiceUrl(data.invoice_url);
        } else {
          setError("Aucune facture liée à cette transaction");
          setInvoiceViewerOpen(false);
        }
      } else {
        setError("Impossible de récupérer la facture");
        setInvoiceViewerOpen(false);
      }
    } catch (e) {
      setError("Erreur lors de la récupération de la facture");
      setInvoiceViewerOpen(false);
    }
    setInvoiceLoading(false);
  };

  // Attach to client
  const handleOpenAttachClient = async (item) => {
    setSelectedItem(item);
    setAttachClientOpen(true);
    
    // Load clients if not already loaded
    if (clients.length === 0) {
      try {
        const token = localStorage.getItem('auth_token');
        const resp = await authFetch(`${API}/api/rachel-v2/import/clients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          // Use single source: pipeline_clients
          setClients(data.pipeline_clients || []);
        }
      } catch (e) {
        console.error("Erreur chargement clients:", e);
      }
    }
  };

  const handleAttachClient = async (clientId) => {
    if (!selectedItem || !clientId) return;
    
    setProcessing(selectedItem.id);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/attach-client`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_id: selectedItem.id,
          client_id: clientId
        })
      });
      
      if (resp.ok) {
        alert("Transaction rattachée au client avec succès");
        setAttachClientOpen(false);
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors du rattachement");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Enhanced attach client handler with payment condition
  const handleAttachClientEnhanced = async (clientId, paymentCondition = null) => {
    if (!selectedItem || !clientId) return;
    
    setProcessing(selectedItem.id);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/attach-client-enhanced`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_id: selectedItem.id,
          client_id: clientId,
          payment_condition: paymentCondition?.id || null,
          payment_condition_label: paymentCondition?.label || null
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        alert(data.message || "Transaction rattachée au client avec succès");
        setAttachClientOpen(false);
        setSelectedItem(null);
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors du rattachement");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Reset page on filter change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [filter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData(currentPage, filter, searchTerm);
  };

  // Change page
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Prepare transactions for validation
  const handlePrepareTransactions = async () => {
    setProcessing('prepare');
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/prepare-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        alert(`${data.prepared} transactions préparées pour validation`);
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors de la préparation");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Sync ledger entries from Pennylane
  const handleSyncPennylane = async () => {
    if (!window.confirm("Synchroniser TOUTES les transactions bancaires depuis Pennylane ?\n\nCette opération peut prendre quelques minutes.")) {
      return;
    }
    
    setProcessing('sync');
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/sync-transactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        const stats = data.stats || {};
        let msg = `✅ Synchronisation terminée\n\n`;
        msg += `📊 Résultat:\n`;
        msg += `- Insérées: ${stats.inserted || 0}\n`;
        msg += `- Mises à jour: ${stats.updated || 0}\n`;
        msg += `- Inchangées: ${stats.unchanged || 0}\n\n`;
        msg += `📈 Total: ${data.total} transactions\n`;
        msg += `- Justifiées: ${data.justified}\n`;
        msg += `- Non justifiées: ${data.not_justified}\n\n`;
        
        if (data.by_account) {
          msg += `🏦 Par compte:\n`;
          Object.entries(data.by_account).forEach(([name, count]) => {
            msg += `- ${name.substring(0, 30)}: ${count}\n`;
          });
        }
        
        alert(msg);
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors de la synchronisation Pennylane");
      }
    } catch (e) {
      setError("Erreur réseau lors de la synchronisation");
    }
    setProcessing(null);
  };

  // Fix data (types, amounts, URLs)
  const handleFixData = async () => {
    if (!window.confirm("Corriger les données ? (types income/expense, montants manquants, URLs)")) {
      return;
    }
    
    setProcessing('fix');
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/fix-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        alert(`Corrections appliquées:\n- Types corrigés: ${data.stats.types_fixed}\n- Montants récupérés: ${data.stats.amounts_fetched}`);
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors de la correction");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Refresh attachment URLs
  const handleRefreshUrls = async () => {
    setProcessing('refresh-urls');
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/refresh-attachment-urls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        alert(`${data.updated} URLs de pièces jointes rafraîchies`);
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors du rafraîchissement");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Rachel IA Search
  const handleRachelSearch = async () => {
    if (!rachelQuery.trim()) {
      alert("Veuillez saisir une recherche");
      return;
    }
    
    setRachelSearching(true);
    setRachelResults(null);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/rachel-search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: rachelQuery,
          date_from: rachelDateFrom || null,
          date_to: rachelDateTo || null
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setRachelResults(data);
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur Rachel IA");
      }
    } catch (e) {
      setError("Erreur réseau Rachel IA");
    }
    setRachelSearching(false);
  };

  // Validate and send to Pennylane
  const handleValidate = async (item) => {
    if (!window.confirm(`Valider et envoyer "${item.supplier_name || item.customer_name}" à Pennylane ?`)) {
      return;
    }
    
    setProcessing(item.id);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ item_id: item.id })
      });
      
      if (resp.ok) {
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors de la validation");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Reject item
  const handleReject = async (item) => {
    const reason = window.prompt("Raison du refus :");
    if (!reason) return;
    
    setProcessing(item.id);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ item_id: item.id, reason })
      });
      
      if (resp.ok) {
        await loadData();
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors du refus");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Update category only (for existing invoices)
  const handleUpdateCategory = async (item, categoryId) => {
    setProcessing(item.id);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await authFetch(`${API}/api/compta-new/update-category`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          item_id: item.id,
          category_id: categoryId
        })
      });
      
      if (resp.ok) {
        alert("Catégorie mise à jour dans Pennylane");
        await loadData(currentPage, filter, searchTerm);
      } else {
        const err = await resp.json();
        setError(err.detail || "Erreur lors de la mise à jour de la catégorie");
      }
    } catch (e) {
      setError("Erreur réseau");
    }
    setProcessing(null);
  };

  // Items are already filtered and paginated from server
  // Use items directly (items = items)

  // Format amount
  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return "-";
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  // Render status badge
  const renderStatus = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.missing_info;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
        ${status === 'ready' ? 'bg-green-500/20 text-green-400' : ''}
        ${status === 'missing_info' ? 'bg-yellow-500/20 text-yellow-400' : ''}
        ${status === 'error' ? 'bg-red-500/20 text-red-400' : ''}
        ${status === 'sent' ? 'bg-blue-500/20 text-blue-400' : ''}
        ${status === 'rejected' ? 'bg-gray-500/20 text-gray-400' : ''}
        ${status === 'existing' ? 'bg-purple-500/20 text-purple-400' : ''}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  // Render category sync status
  const renderCategorySyncStatus = (item) => {
    if (!item.category_id) return null;
    
    if (item.category_synced) {
      return <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Sync</span>;
    } else if (item.category_modified) {
      return <span className="text-orange-400 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Non envoyé</span>;
    } else if (item.category_error) {
      return <span className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Erreur</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <ClipboardCheck className="w-7 h-7 text-orange-400" />
            File d'attente Pennylane
          </h1>
          <p className="text-slate-400 mt-1">
            Validation humaine avant envoi à Pennylane
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Rachel IA Button */}
          <button
            onClick={() => setRachelSearchOpen(!rachelSearchOpen)}
            className={`px-4 py-2 ${rachelSearchOpen ? 'bg-purple-600' : 'bg-purple-700 hover:bg-purple-600'} rounded-lg flex items-center gap-2 text-sm font-medium`}
          >
            <Sparkles className="w-4 h-4" />
            Rachel IA
          </button>
          
          {/* Fix Data Button */}
          <button
            onClick={handleFixData}
            disabled={processing === 'fix'}
            className="px-3 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg flex items-center gap-2 text-sm"
            title="Corriger types et montants"
          >
            {processing === 'fix' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wrench className="w-4 h-4" />
            )}
            Corriger données
          </button>
          
          {/* Refresh URLs Button */}
          <button
            onClick={handleRefreshUrls}
            disabled={processing === 'refresh-urls'}
            className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 rounded-lg flex items-center gap-2 text-sm"
            title="Rafraîchir les liens des pièces jointes"
          >
            {processing === 'refresh-urls' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4" />
            )}
            Liens
          </button>
          
          <button
            onClick={handleSyncPennylane}
            disabled={processing === 'sync'}
            className="px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-lg flex items-center gap-2 text-sm font-medium"
          >
            {processing === 'sync' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Sync
          </button>
          
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Rachel IA Search Panel */}
      {rachelSearchOpen && (
        <div className="bg-gradient-to-r from-purple-900/40 to-slate-800/60 border border-purple-500/30 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-200">Rachel IA - Recherche intelligente</h3>
              <p className="text-sm text-slate-400">Recherchez par nom, montant, ou description dans les factures</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <input
                type="text"
                value={rachelQuery}
                onChange={(e) => setRachelQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRachelSearch()}
                placeholder="Ex: LEROY MERLIN, facture 1234, virement..."
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={rachelDateFrom}
                onChange={(e) => setRachelDateFrom(e.target.value)}
                className="flex-1 px-2 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm"
                placeholder="Du"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-400">→</span>
              <input
                type="date"
                value={rachelDateTo}
                onChange={(e) => setRachelDateTo(e.target.value)}
                className="flex-1 px-2 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm"
                placeholder="Au"
              />
              <button
                onClick={handleRachelSearch}
                disabled={rachelSearching}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg flex items-center gap-2"
              >
                {rachelSearching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          
          {/* Rachel Results */}
          {rachelResults && (
            <div className="mt-4 space-y-3">
              <div className="bg-purple-800/30 border border-purple-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-purple-100">{rachelResults.rachel_response}</p>
                    {rachelResults.suggestions?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {rachelResults.suggestions.map((sug, i) => (
                          <span key={i} className="text-xs bg-purple-700/50 px-2 py-1 rounded-full text-purple-200">
                            💡 {sug}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {rachelResults.results?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-400">{rachelResults.total_found} résultat(s) trouvé(s):</p>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {rachelResults.results.map((result, idx) => (
                      <div key={idx} className="bg-slate-800/60 border border-slate-600/50 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{result.supplier_name || result.customer_name || result.description}</p>
                          <p className="text-sm text-slate-400">
                            {result.date} • {result.bank_amount || result.amount_ttc}€ • {result.category_name || 'Sans catégorie'}
                          </p>
                        </div>
                        {result.attachment_filename && (
                          <span className="text-xs bg-blue-700/50 px-2 py-1 rounded text-blue-200 flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            PDF
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-300">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { key: 'ready', label: 'Prêt', icon: CheckCircle2, color: 'green' },
          { key: 'missing_info', label: 'Manque info', icon: AlertTriangle, color: 'yellow' },
          { key: 'error', label: 'Erreur', icon: AlertCircle, color: 'red' },
          { key: 'existing', label: 'Existant PL', icon: ExternalLink, color: 'purple' },
          { key: 'sent', label: 'Envoyé', icon: Send, color: 'blue' }
        ].map(({ key, label, icon: Icon, color }) => (
          <button
            key={key}
            onClick={() => setFilter(filter === key ? 'all' : key)}
            className={`p-4 rounded-xl border transition ${
              filter === key 
                ? `bg-${color}-500/20 border-${color}-500/50` 
                : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <Icon className={`w-5 h-5 text-${color}-400`} />
              <div className="text-left">
                <p className={`text-2xl font-bold text-${color}-400`}>{stats[key] || 0}</p>
                <p className="text-xs text-slate-400">{label}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-slate-800/50 rounded-xl p-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher par nom, description, montant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500"
          />
        </div>
        
        {/* Account filter */}
        <select
          value={accountFilter}
          onChange={(e) => {
            setAccountFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500 max-w-[200px]"
        >
          <option value="all">Tous les comptes</option>
          {bankAccounts.map((acc, idx) => (
            <option key={idx} value={acc}>{acc.substring(0, 25)}{acc.length > 25 ? '...' : ''}</option>
          ))}
        </select>
        
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
        >
          <option value="all">Tous les statuts</option>
          <option value="ready">🟢 Prêt</option>
          <option value="missing_info">🟡 Manque info</option>
          <option value="error">🔴 Erreur</option>
          <option value="existing">🟣 Existant PL</option>
          <option value="sent">🔵 Envoyé</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-x-auto">
        <table className="w-full min-w-[1500px]">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-900/50">
              <th className="text-left p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Type</th>
              <th className="text-left p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Date</th>
              <th className="text-left p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Compte</th>
              <th className="text-left p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Mode</th>
              <th className="text-left p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Tiers</th>
              <th className="text-right p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Montant</th>
              <th className="text-left p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Catégorie</th>
              <th className="text-center p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Pièce</th>
              <th className="text-left p-3 text-sm font-medium text-slate-400 whitespace-nowrap">Statut</th>
              <th className="text-center p-3 text-sm font-medium text-slate-400 whitespace-nowrap sticky right-0 bg-slate-900/90">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-8 text-center text-slate-500">
                  <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune transaction en attente de validation</p>
                  <p className="text-sm mt-1">Cliquez sur "Sync" pour charger les transactions depuis Pennylane</p>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const typeConfig = item.type === 'expense' ? TRANSACTION_TYPES.expense : TRANSACTION_TYPES.income;
                const TypeIcon = typeConfig.icon;
                const isProcessing = processing === item.id;
                
                // Detect amount mismatch
                // Use bank_amount directly to preserve the sign (positive for income, negative for expense)
                const bankAmount = item.bank_amount ?? 0;
                const invoiceAmount = item.amount_ttc || 0;
                const amountMismatch = bankAmount && invoiceAmount && Math.abs(Math.abs(bankAmount) - invoiceAmount) > 0.01;
                
                // Payment method icons
                const getPaymentMethodStyle = (method) => {
                  switch(method) {
                    case 'CB': return 'bg-blue-500/20 text-blue-400';
                    case 'Virement': return 'bg-green-500/20 text-green-400';
                    case 'Prélèvement': return 'bg-purple-500/20 text-purple-400';
                    case 'Chèque': return 'bg-yellow-500/20 text-yellow-400';
                    case 'Frais': return 'bg-red-500/20 text-red-400';
                    case 'Espèces': return 'bg-orange-500/20 text-orange-400';
                    default: return 'bg-slate-500/20 text-slate-400';
                  }
                };
                
                return (
                  <tr key={item.id} className={`border-b border-slate-700/50 hover:bg-slate-700/30 transition ${item.is_bank_fee ? 'bg-red-900/10' : ''} ${item.is_internal_transfer ? 'bg-blue-900/10' : ''}`}>
                    {/* Type */}
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                        ${item.type === 'expense' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}
                      `}>
                        <TypeIcon className="w-3.5 h-3.5" />
                        {typeConfig.label}
                      </span>
                      {item.is_bank_fee && (
                        <span className="ml-1 px-1.5 py-0.5 bg-red-600/30 text-red-300 rounded text-[10px]">FRAIS</span>
                      )}
                      {item.is_internal_transfer && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600/30 text-blue-300 rounded text-[10px]">INTERNE</span>
                      )}
                    </td>
                    
                    {/* Date */}
                    <td className="p-3 text-sm text-white whitespace-nowrap">{formatDate(item.date)}</td>
                    
                    {/* Compte bancaire */}
                    <td className="p-3">
                      <span className="text-xs text-slate-400 truncate max-w-[100px] block" title={item.bank_account_name || 'Inconnu'}>
                        {item.bank_account_name ? item.bank_account_name.substring(0, 15) + (item.bank_account_name.length > 15 ? '...' : '') : '-'}
                      </span>
                    </td>
                    
                    {/* Mode de paiement */}
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPaymentMethodStyle(item.payment_method)}`}>
                        {item.payment_method || 'Autre'}
                      </span>
                    </td>
                    
                    {/* Tiers */}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {item.type === 'expense' ? (
                          <Building2 className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        ) : (
                          <User className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        )}
                        <span className="font-medium text-white truncate max-w-[180px]" title={item.supplier_name || item.customer_name || item.label || '-'}>
                          {item.supplier_name || item.customer_name || '-'}
                        </span>
                      </div>
                    </td>
                    
                    {/* Montant avec sens */}
                    <td className="p-3 text-right">
                      <div className="flex flex-col items-end">
                        <span className={`text-sm font-bold ${bankAmount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {bankAmount >= 0 ? '+' : ''}{formatAmount(bankAmount)}
                        </span>
                        <span className="text-xs text-slate-500">
                          {item.type === 'income' ? '↓ Entrée' : '↑ Sortie'}
                        </span>
                      </div>
                    </td>
                    
                    {/* Catégorie */}
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        {item.category_name ? (
                          <>
                            <span className="text-sm text-purple-400 flex items-center gap-1 truncate max-w-[150px]" title={item.category_name}>
                              <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" />
                              {item.category_name}
                            </span>
                            {renderCategorySyncStatus(item)}
                          </>
                        ) : (
                          <span className="text-sm text-slate-500">Non assignée</span>
                        )}
                      </div>
                    </td>
                    
                    {/* Pièce comptable */}
                    <td className="p-3 text-center">
                      {item.attachment_url ? (
                        <a
                          href={item.attachment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded text-xs font-medium transition"
                          title={item.attachment_filename || 'Voir la pièce'}
                        >
                          <FileText className="w-3.5 h-3.5" />
                          PDF
                        </a>
                      ) : (
                        <span className="text-slate-600">-</span>
                      )}
                    </td>
                    
                    {/* Statut */}
                    <td className="p-4">
                      {renderStatus(item.status)}
                      {item.error_message && (
                        <p className="text-xs text-red-400 mt-1 max-w-[150px] truncate" title={item.error_message}>
                          {item.error_message}
                        </p>
                      )}
                    </td>
                    
                    {/* Actions */}
                    <td className="p-4 sticky right-0 bg-slate-800/95">
                      <div className="flex items-center justify-center gap-1">
                        {isProcessing ? (
                          <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
                        ) : (
                          <>
                            {/* View invoice button */}
                            {item.is_justified && (
                              <button
                                onClick={() => handleViewInvoice(item)}
                                className="p-1.5 bg-blue-600/30 hover:bg-blue-600/50 rounded text-blue-400 hover:text-white transition"
                                title="Voir la facture"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                            )}
                            
                            {/* Attach to client button */}
                            <button
                              onClick={() => handleOpenAttachClient(item)}
                              className="p-1.5 bg-purple-600/30 hover:bg-purple-600/50 rounded text-purple-400 hover:text-white transition"
                              title="Rattacher à un client"
                            >
                              <Users className="w-3.5 h-3.5" />
                            </button>
                            
                            {/* Edit button */}
                            <button
                              onClick={() => { setSelectedItem(item); setEditModalOpen(true); }}
                              className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 hover:text-white transition"
                              title="Modifier"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            
                            {/* Validate button */}
                            <button
                              onClick={() => handleValidate(item)}
                              className="p-1.5 bg-green-600/30 hover:bg-green-600 rounded text-green-400 hover:text-white transition"
                              title="Valider"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            
                            {/* Reject button */}
                            <button
                              onClick={() => handleReject(item)}
                              className="p-1.5 bg-red-600/30 hover:bg-red-600 rounded text-red-400 hover:text-white transition"
                              title="Refuser"
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-slate-800/30 rounded-xl p-4">
          <div className="text-sm text-slate-400">
            Affichage {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems} transactions
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm"
            >
              ««
            </button>
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm"
            >
              «
            </button>
            
            <div className="flex items-center gap-1">
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 rounded text-sm ${
                      currentPage === pageNum
                        ? 'bg-orange-600 text-white'
                        : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm"
            >
              »
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm"
            >
              »»
            </button>
          </div>
          
          <div className="text-sm text-slate-400">
            Page {currentPage} / {totalPages}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-slate-800/30 rounded-xl p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Légende
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <ArrowUpRight className="w-4 h-4 text-red-400" />
            <span className="text-slate-400">Décaissement = Facture fournisseur</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowDownLeft className="w-4 h-4 text-green-400" />
            <span className="text-slate-400">Encaissement = Facture client</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <span className="text-slate-400">Catégorie synchronisée</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            <span className="text-slate-400">Modifiée localement</span>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && selectedItem && (
        <ValidationEditModal
          item={selectedItem}
          categories={categories}
          onClose={() => { setEditModalOpen(false); setSelectedItem(null); }}
          onSave={async (updatedItem) => {
            try {
              const token = localStorage.getItem('auth_token');
              const resp = await authFetch(`${API}/api/compta-new/update-item`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatedItem)
              });
              
              if (resp.ok) {
                await loadData();
                setEditModalOpen(false);
                setSelectedItem(null);
              } else {
                const err = await resp.json();
                setError(err.detail || "Erreur lors de la mise à jour");
              }
            } catch (e) {
              setError("Erreur réseau");
            }
          }}
        />
      )}

      {/* Invoice Viewer Modal */}
      {invoiceViewerOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                Visualisation de la facture
              </h3>
              <button
                onClick={() => { setInvoiceViewerOpen(false); setInvoiceUrl(null); }}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {invoiceLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : invoiceUrl ? (
                <iframe
                  src={invoiceUrl}
                  className="w-full h-full min-h-[500px] rounded-lg bg-white"
                  title="Facture"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <p>Aucune facture disponible</p>
                </div>
              )}
            </div>
            {invoiceUrl && (
              <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
                <a
                  href={invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ouvrir dans un nouvel onglet
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Attach to Client Modal - Enhanced with IA Suggestions */}
      {attachClientOpen && selectedItem && (
        <AttachClientModalEnhanced
          item={selectedItem}
          clients={clients}
          onClose={() => { setAttachClientOpen(false); setSelectedItem(null); }}
          onAttach={async (clientId, paymentCondition) => {
            await handleAttachClientEnhanced(clientId, paymentCondition);
          }}
        />
      )}
    </div>
  );
}

/**
 * Modal d'édition d'un item de validation
 */
function ValidationEditModal({ item, categories, onClose, onSave }) {
  const [formData, setFormData] = useState({
    id: item.id,
    type: item.type || 'expense',
    supplier_name: item.supplier_name || '',
    customer_name: item.customer_name || '',
    amount_ht: item.amount_ht || '',
    amount_tva: item.amount_tva || '',
    amount_ttc: item.amount_ttc || '',
    taux_tva: item.taux_tva || 20,
    category_id: item.category_id || '',
    category_name: item.category_name || '',
    date: item.date?.split('T')[0] || '',
    description: item.description || '',
    notes: item.notes || '',
    numero_facture: item.numero_facture || ''
  });
  const [saving, setSaving] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  
  // Upload et analyse IA
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const filteredCategories = categorySearch
    ? categories.filter(c => c.name?.toLowerCase().includes(categorySearch.toLowerCase()))
    : categories;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  // Auto-calculate TTC when HT or TVA changes
  const handleAmountChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const newData = { ...formData, [field]: value };
    
    if (field === 'amount_ht' || field === 'amount_tva') {
      const ht = field === 'amount_ht' ? numValue : (parseFloat(formData.amount_ht) || 0);
      const tva = field === 'amount_tva' ? numValue : (parseFloat(formData.amount_tva) || 0);
      newData.amount_ttc = (ht + tva).toFixed(2);
    }
    
    setFormData(newData);
  };

  const selectCategory = (cat) => {
    setFormData({
      ...formData,
      category_id: cat.pennylane_id,
      category_name: cat.name
    });
    setCategorySearch('');
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-orange-400" />
              Modifier la transaction
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {item.status === 'existing' 
                ? "Seule la catégorie peut être modifiée pour les factures existantes" 
                : "Modifiez les informations avant validation"}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Type de transaction</label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'expense' })}
                disabled={item.status === 'existing'}
                className={`flex-1 p-3 rounded-lg border flex items-center justify-center gap-2 transition ${
                  formData.type === 'expense'
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'border-slate-700 text-slate-400 hover:border-slate-600'
                } ${item.status === 'existing' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <ArrowUpRight className="w-4 h-4" />
                Décaissement (Fournisseur)
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'income' })}
                disabled={item.status === 'existing'}
                className={`flex-1 p-3 rounded-lg border flex items-center justify-center gap-2 transition ${
                  formData.type === 'income'
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'border-slate-700 text-slate-400 hover:border-slate-600'
                } ${item.status === 'existing' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <ArrowDownLeft className="w-4 h-4" />
                Encaissement (Client)
              </button>
            </div>
          </div>

          {/* Tiers */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {formData.type === 'expense' ? 'Fournisseur' : 'Client'}
            </label>
            <input
              type="text"
              value={formData.type === 'expense' ? formData.supplier_name : formData.customer_name}
              onChange={(e) => setFormData({
                ...formData,
                [formData.type === 'expense' ? 'supplier_name' : 'customer_name']: e.target.value
              })}
              disabled={item.status === 'existing'}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 disabled:opacity-50"
              placeholder="Nom du tiers"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              disabled={item.status === 'existing'}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500 disabled:opacity-50"
            />
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Montant HT</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount_ht}
                onChange={(e) => handleAmountChange('amount_ht', e.target.value)}
                disabled={item.status === 'existing'}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500 disabled:opacity-50"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Taux TVA (%)</label>
              <select
                value={formData.taux_tva}
                onChange={(e) => {
                  const taux = parseFloat(e.target.value);
                  const ht = parseFloat(formData.amount_ht) || 0;
                  const tva = ht * (taux / 100);
                  setFormData({
                    ...formData,
                    taux_tva: taux,
                    amount_tva: tva.toFixed(2),
                    amount_ttc: (ht + tva).toFixed(2)
                  });
                }}
                disabled={item.status === 'existing'}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500 disabled:opacity-50"
              >
                <option value="20">20%</option>
                <option value="10">10%</option>
                <option value="5.5">5.5%</option>
                <option value="2.1">2.1%</option>
                <option value="0">0%</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">TVA</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount_tva}
                onChange={(e) => handleAmountChange('amount_tva', e.target.value)}
                disabled={item.status === 'existing'}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500 disabled:opacity-50"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">TTC</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount_ttc}
                disabled
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white opacity-75"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Upload et Analyse IA de facture */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <h3 className="font-medium text-purple-300 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Ajouter un justificatif & Analyse IA
            </h3>
            
            {!uploadedFile ? (
              <div className="border-2 border-dashed border-purple-500/30 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    setUploadedFile(file);
                    setAnalyzing(true);
                    
                    // Analyser avec l'IA
                    try {
                      const token = localStorage.getItem('auth_token');
                      const formDataUpload = new FormData();
                      formDataUpload.append('file', file);
                      formDataUpload.append('transaction_id', item.id);
                      
                      const resp = await authFetch(`${API}/api/compta-new/analyze-invoice`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: formDataUpload
                      });
                      
                      if (resp.ok) {
                        const data = await resp.json();
                        setAnalysisResult(data.extracted_data);
                        
                        // Auto-remplir les champs
                        if (data.extracted_data) {
                          const ext = data.extracted_data;
                          setFormData(prev => ({
                            ...prev,
                            supplier_name: ext.fournisseur || prev.supplier_name,
                            amount_ht: ext.montant_ht || prev.amount_ht,
                            amount_tva: ext.montant_tva || prev.amount_tva,
                            amount_ttc: ext.montant_ttc || prev.amount_ttc,
                            taux_tva: ext.taux_tva || prev.taux_tva,
                            date: ext.date_facture || prev.date,
                            description: ext.description || prev.description,
                            numero_facture: ext.numero_facture || prev.numero_facture
                          }));
                        }
                      }
                    } catch (err) {
                      console.error('Erreur analyse:', err);
                    }
                    setAnalyzing(false);
                  }}
                  className="hidden"
                  id="invoice-upload"
                  disabled={item.status === 'existing'}
                />
                <label 
                  htmlFor="invoice-upload"
                  className={`cursor-pointer ${item.status === 'existing' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FileText className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                  <p className="text-purple-300 font-medium">Cliquez pour ajouter une facture</p>
                  <p className="text-sm text-slate-400 mt-1">PDF ou image (JPG, PNG)</p>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-purple-400" />
                    <div>
                      <p className="text-white font-medium">{uploadedFile.name}</p>
                      <p className="text-xs text-slate-400">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setUploadedFile(null); setAnalysisResult(null); }}
                    className="p-2 hover:bg-red-500/20 rounded text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                {analyzing && (
                  <div className="flex items-center justify-center gap-2 py-3 text-purple-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Analyse IA en cours...</span>
                  </div>
                )}
                
                {analysisResult && !analyzing && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <p className="text-green-400 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Données extraites automatiquement - Vérifiez et corrigez si nécessaire
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Numéro de facture */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Numéro de facture</label>
            <input
              type="text"
              value={formData.numero_facture}
              onChange={(e) => setFormData({ ...formData, numero_facture: e.target.value })}
              disabled={item.status === 'existing'}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 disabled:opacity-50"
              placeholder="N° de facture"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Catégorie / Dossier Client
              {formData.category_name && (
                <span className="ml-2 text-purple-400">
                  Actuel: {formData.category_name}
                </span>
              )}
            </label>
            <input
              type="text"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500"
              placeholder="Rechercher une catégorie..."
            />
            {categorySearch && (
              <div className="mt-2 max-h-40 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg">
                {filteredCategories.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500">Aucune catégorie trouvée</p>
                ) : (
                  filteredCategories.slice(0, 20).map((cat) => (
                    <button
                      key={cat.pennylane_id}
                      type="button"
                      onClick={() => selectCategory(cat)}
                      className="w-full p-3 text-left hover:bg-slate-700 text-sm text-white border-b border-slate-700 last:border-0"
                    >
                      {cat.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500"
              placeholder="Description de la transaction"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Notes internes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 resize-none"
              placeholder="Notes internes (non envoyées à Pennylane)"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-lg text-white font-medium flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Enregistrer
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Soumettre cette transaction vers Pennylane ?')) return;
                setSaving(true);
                // D'abord enregistrer puis valider
                await onSave({ ...formData, submit_to_pennylane: true });
                setSaving(false);
              }}
              disabled={saving || item.status === 'existing'}
              className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white font-medium flex items-center justify-center gap-2"
              title={item.status === 'existing' ? "Cette facture existe déjà dans Pennylane" : "Enregistrer et envoyer vers Pennylane"}
            >
              <Send className="w-4 h-4" />
              Vers Pennylane
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


/**
 * Modal améliorée pour rattacher une transaction à un client
 * Fonctionnalités:
 * - Suggestions IA basées sur le libellé
 * - Recherche textuelle
 * - Pour encaissements: Sélection de l'objet de règlement
 */
function AttachClientModalEnhanced({ item, clients, onClose, onAttach }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [paymentConditions, setPaymentConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [analysis, setAnalysis] = useState('');

  const isIncome = item.type === 'income' || (item.bank_amount && item.bank_amount > 0);

  // Charger les conditions de règlement
  useEffect(() => {
    const loadConditions = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const resp = await authFetch(`${API}/api/compta-new/payment-conditions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setPaymentConditions(data.conditions || []);
        }
      } catch (e) {
        console.error('Erreur chargement conditions:', e);
      }
    };
    if (isIncome) {
      loadConditions();
    }
  }, [isIncome]);

  // Charger les suggestions IA
  useEffect(() => {
    const loadSuggestions = async () => {
      if (!item?.label && !item?.id) return;
      
      setLoadingSuggestions(true);
      try {
        const token = localStorage.getItem('auth_token');
        const resp = await authFetch(`${API}/api/compta-new/suggest-clients`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            transaction_id: item.id,
            label: item.label || '',
            amount: item.bank_amount || 0
          })
        });
        
        if (resp.ok) {
          const data = await resp.json();
          setSuggestions(data.suggestions || []);
          setAnalysis(data.analysis || '');
        }
      } catch (e) {
        console.error('Erreur suggestions IA:', e);
      }
      setLoadingSuggestions(false);
    };
    
    loadSuggestions();
  }, [item]);

  // Filtrer les clients par recherche
  const filteredClients = searchTerm.length >= 2
    ? clients.filter(c => {
        const name = (c.name || c.nom || `${c.prenom || ''} ${c.nom || ''}`).toLowerCase();
        return name.includes(searchTerm.toLowerCase());
      })
    : [];

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearchTerm(client.name || client.nom || `${client.prenom || ''} ${client.nom || ''}`);
  };

  const handleAttach = async () => {
    if (!selectedClient) {
      alert('Veuillez sélectionner un client');
      return;
    }
    
    if (isIncome && !selectedCondition) {
      alert('Veuillez sélectionner un objet de règlement pour cet encaissement');
      return;
    }
    
    setAttaching(true);
    await onAttach(selectedClient.id, selectedCondition);
    setAttaching(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            Rattacher à un client
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Transaction info */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <p className="text-sm text-slate-400 mb-1">Transaction</p>
            <p className="text-white font-medium">{item.label?.substring(0, 80)}{item.label?.length > 80 ? '...' : ''}</p>
            <div className="flex items-center gap-4 mt-2">
              <span className={`text-lg font-bold ${item.bank_amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {item.bank_amount >= 0 ? '+' : ''}{item.bank_amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </span>
              <span className={`text-xs px-2 py-1 rounded-full ${isIncome ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {isIncome ? 'Encaissement' : 'Décaissement'}
              </span>
            </div>
          </div>

          {/* Rachel IA Suggestions */}
          {loadingSuggestions ? (
            <div className="flex items-center justify-center py-6 gap-3 text-purple-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Rachel IA analyse le libellé...</span>
            </div>
          ) : suggestions.length > 0 && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-400" />
                <span className="font-medium text-purple-300">Suggestions Rachel IA</span>
              </div>
              {analysis && (
                <p className="text-sm text-slate-400 mb-3 italic">{analysis}</p>
              )}
              <div className="space-y-2">
                {suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectClient({ id: s.client_id, name: s.client_name })}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center justify-between transition ${
                      selectedClient?.id === s.client_id
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-800 hover:bg-slate-700'
                    }`}
                  >
                    <div>
                      <p className="font-medium">{s.client_name}</p>
                      <p className="text-sm text-slate-400">{s.reason}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm px-2 py-1 bg-purple-500/30 rounded-full">
                        {Math.round(s.confidence * 100)}%
                      </span>
                      {selectedClient?.id === s.client_id && <Check className="w-5 h-5" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search field */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <Search className="w-4 h-4 inline-block mr-1" />
              Rechercher un client
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (selectedClient && e.target.value !== (selectedClient.name || selectedClient.nom)) {
                  setSelectedClient(null);
                }
              }}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500"
              placeholder="Tapez au moins 2 caractères..."
            />
            
            {/* Search results */}
            {filteredClients.length > 0 && !selectedClient && (
              <div className="mt-2 max-h-48 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg">
                {filteredClients.slice(0, 10).map((client, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectClient(client)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-700 border-b border-slate-700 last:border-0"
                  >
                    <p className="font-medium text-white">
                      {client.name || client.nom || `${client.prenom || ''} ${client.nom || ''}`}
                    </p>
                    {client.ville && <p className="text-sm text-slate-400">{client.ville}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected client confirmation */}
          {selectedClient && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-green-300">Client sélectionné</p>
                  <p className="text-white">{selectedClient.name || selectedClient.nom}</p>
                </div>
              </div>
              <button
                onClick={() => { setSelectedClient(null); setSearchTerm(''); }}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Payment condition (only for income) */}
          {isIncome && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <Tag className="w-4 h-4 inline-block mr-1" />
                Objet de règlement <span className="text-red-400">*</span>
              </label>
              <select
                value={selectedCondition?.id || ''}
                onChange={(e) => {
                  const cond = paymentConditions.find(c => c.id === e.target.value);
                  setSelectedCondition(cond || null);
                }}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">-- Sélectionner l'objet de règlement --</option>
                {paymentConditions.map(cond => (
                  <option key={cond.id} value={cond.id}>{cond.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t border-slate-700">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-medium"
            >
              Annuler
            </button>
            <button
              onClick={handleAttach}
              disabled={!selectedClient || attaching || (isIncome && !selectedCondition)}
              className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg text-white font-medium flex items-center justify-center gap-2"
            >
              {attaching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Rattacher
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
